import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { unwrap } from '@/api/client';
import type { UserDto } from '@/api/types';
import { fieldError } from '@/api/errors';
import { useAuthStatus, useServices } from '@/app/servicesContext';
import { Avatar, AvatarPicker } from '@/ui/Avatar';
import { Button, LinkButton } from '@/ui/Button';
import { Card, Hint } from '@/ui/Card';
import { PhotoPicker } from '@/ui/PhotoPicker';
import { useToast } from '@/ui/toast';
import { ErrorNote } from '@/ui/ErrorNote';
import { DEFAULT_PRESET, PRESET_KEYS } from '@/ui/presetCatalog';
import { TextField } from '@/ui/TextField';
import { photoForm } from '@/lib/imageUpload';
import { AuthLayout } from './AuthLayout';
import { queryKeys, useAvatarPresets, useMeta } from './queries';
import { useCaptcha } from './useCaptcha';
import { useAfterSignInTarget, useCompleteSignIn } from './useSignIn';

const NAME_MIN = 2;
const NAME_MAX = 30;

export function RegisterPage() {
  const status = useAuthStatus();
  const location = useLocation();
  const phone = (location.state as { phone?: string } | null)?.phone;
  const { publicApi, api, queryClient } = useServices();
  const toast = useToast();
  const meta = useMeta();
  const presets = useAvatarPresets();
  const captcha = useCaptcha(meta.data);
  const completeSignIn = useCompleteSignIn();
  const target = useAfterSignInTarget();
  const [displayName, setDisplayName] = useState('');
  const [avatarPreset, setAvatarPreset] = useState(DEFAULT_PRESET);
  const [acceptTerms, setAcceptTerms] = useState(false);
  // A foto escolhida (já reduzida) e o endereço local dela, só para mostrar a prévia. Ela é enviada logo depois de a conta existir.
  const [photo, setPhoto] = useState<Blob | null>(null);
  const photoUrl = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);

  useEffect(
    () => () => {
      if (photoUrl) {
        URL.revokeObjectURL(photoUrl);
      }
    },
    [photoUrl],
  );

  /** Com a conta criada e a sessão aberta, manda a foto. Se falhar, a conta já existe: a pessoa tenta de novo no perfil. */
  const sendPhoto = async (picked: Blob) => {
    try {
      const user: UserDto = await unwrap(
        api.PUT('/api/v1/users/me/avatar', { body: { file: '' }, bodySerializer: () => photoForm(picked) }),
      );
      queryClient.setQueryData(queryKeys.me, user);
    } catch {
      toast.show(
        'Sua conta foi criada, mas não deu para enviar a foto. Você pode tentar de novo no seu perfil.',
        'error',
      );
    }
  };

  const register = useMutation({
    mutationFn: () =>
      unwrap(
        publicApi.POST('/api/v1/auth/register', {
          body: {
            phone: phone ?? '',
            displayName: displayName.trim(),
            avatarPreset,
            acceptTerms,
            captchaToken: captcha.token ?? undefined,
          },
        }),
      ),
    onSuccess: (auth) => {
      completeSignIn(auth);
      if (photo) {
        void sendPhoto(photo);
      }
    },
    onError: () => captcha.reset(),
  });

  if (status === 'authenticated') {
    return <Navigate to={target} replace />;
  }

  if (!phone) {
    return <Navigate to="/entrar" replace />;
  }

  if (meta.data?.auth.registrationOpen === false) {
    return (
      <AuthLayout>
        <Card>
          <h2>Cadastros fechados</h2>
          <Hint>No momento não estamos aceitando contas novas. Peça ajuda a quem te convidou.</Hint>
          <LinkButton to="/entrar" icon="back">
            Voltar
          </LinkButton>
        </Card>
      </AuthLayout>
    );
  }

  const keys = presets.data?.keys?.length ? presets.data.keys : PRESET_KEYS;
  const trimmed = displayName.trim();
  const canSubmit = trimmed.length >= NAME_MIN && acceptTerms && captcha.ready;
  const nameError = fieldError(register.error, 'displayName');

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (canSubmit && !register.isPending) {
      register.mutate();
    }
  };

  return (
    <AuthLayout>
      <Card>
        <h2>Vamos criar a sua conta</h2>
        <Hint>
          Não encontramos uma conta com o número {phone}. É rapidinho: nome e um avatar ou uma foto.
        </Hint>
        <form onSubmit={onSubmit} className="stack" noValidate>
          <TextField
            label="Seu nome"
            autoComplete="given-name"
            maxLength={NAME_MAX}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            hint="É o nome que seus amigos vão ver (de 2 a 30 letras)."
            error={nameError}
            autoFocus
          />
          <AvatarPicker keys={keys} value={avatarPreset} onChange={setAvatarPreset} />
          <PhotoPicker
            hasPhoto={photo !== null}
            busy={register.isPending}
            onPick={setPhoto}
            onRemove={() => setPhoto(null)}
          />
          {photo ? (
            <Hint>A foto escolhida substitui o avatar. Dá para trocá-la depois, no perfil.</Hint>
          ) : null}
          {trimmed || photo ? (
            <div className="row">
              <Avatar
                avatar={
                  photoUrl
                    ? { kind: 'photo', preset: null, url: photoUrl }
                    : { kind: 'preset', preset: avatarPreset, url: null }
                }
                name={trimmed || 'Você'}
                size="lg"
              />
              <p>
                <strong>{trimmed}</strong>
              </p>
            </div>
          ) : null}
          <label className="check">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(event) => setAcceptTerms(event.target.checked)}
            />
            <span>
              Li e aceito os <Link to="/termos">termos de uso</Link> e o{' '}
              <Link to="/privacidade">aviso de privacidade</Link>.
            </span>
          </label>
          {captcha.widget}
          {register.error && !nameError ? <ErrorNote error={register.error} /> : null}
          <Button type="submit" variant="go" size="lg" loading={register.isPending} disabled={!canSubmit}>
            Criar conta e entrar
          </Button>
        </form>
      </Card>
      <LinkButton to="/entrar" variant="light" size="sm" icon="back">
        Usar outro número
      </LinkButton>
    </AuthLayout>
  );
}
